// C.1 — Подавление (suppression), keyword Глобала.
//
// Правило (диздок v2.9, новая аура вместо Цепи командования):
//   Источник Подавления проецирует дебаф на зеркальную ячейку врага
//   (та же линия + та же позиция):
//     - 1-я линия (line=0): -1 атаки противоположному вражескому юниту.
//       Аура, действует пока источник жив на этой ячейке. Снимается
//       автоматически при смерти / возврате в руку / перемещении источника.
//     - 2-я линия (line=1): Молчание на 2 хода (oneshot при выставлении
//       источника).
//
//   Если зеркальная ячейка пуста — эффект ждёт, не применяется.
//
//   Усиление от Героя «Дирижёр» Райна (global_rayna): пока Райна жива,
//   эффект Подавления любого союзного источника удваивается (на 1-й
//   -2 атаки, на 2-й Молчание на 3 хода). Управляется флагом
//   state.suppressionAmplifierActive[playerIdx]=true.
//
//   Расширение от Резонанс (Legendary): пока Резонанс жив, Подавление
//   союзных Глобал-юнитов применяется к двум вражеским ячейкам — зеркальная
//   ячейка + такая же ячейка на другой линии того же столбца. Управляется
//   флагом state.suppressionExtenderActive[playerIdx]=true.
//
// Под Молчанием самого источника Подавление автоматически снимается —
// silence.js чистит derivedKeywords и keywords, после чего наш
// recomputeSuppression перестанет считать его источником.

import { applyTemporaryBuff, removePermanentBuffsBySource } from '../buffs.js';
import { applySilence, removeSilence } from './silence.js';
import { KW } from '../keywords.js';

const SUPPRESSION_SOURCE = 'suppression';

function suppressionMatcher(sourceId) {
  return typeof sourceId === 'string' && sourceId.startsWith(SUPPRESSION_SOURCE + ':');
}

// Юнит является активным источником Подавления, если он имеет keyword
// suppression и не молчит.
function isSuppressionSource(unit) {
  if (!unit) return false;
  if (unit.current?.silenced) return false;
  const keywords = unit.keywords || [];
  return keywords.includes(KW.SUPPRESSION);
}

function amplifierActive(state, ownerIdx) {
  return !!(state?.suppressionAmplifierActive?.[ownerIdx]);
}

function extenderActive(state, ownerIdx) {
  return !!(state?.suppressionExtenderActive?.[ownerIdx]);
}

// Зеркальная ячейка врага: та же линия + тот же слот, у соперника.
function getMirrorCell(state, sourceOwnerIdx, source) {
  const enemyIdx = 1 - sourceOwnerIdx;
  const enemy = state.players?.[enemyIdx];
  if (!enemy?.field) return null;
  const line = source.line;
  const slot = source.slot;
  if (line == null || slot == null) return null;
  const target = enemy.field[line]?.[slot];
  return target || null;
}

// Ячейка «другой линии того же столбца» у врага (для Резонанс-расширения).
function getOtherLineCell(state, sourceOwnerIdx, source) {
  const enemyIdx = 1 - sourceOwnerIdx;
  const enemy = state.players?.[enemyIdx];
  if (!enemy?.field) return null;
  const line = source.line;
  const slot = source.slot;
  if (line == null || slot == null) return null;
  const otherLine = 1 - line;
  const target = enemy.field[otherLine]?.[slot];
  return target || null;
}

// Применить дебаф 1-й линии к одной цели. atk-делта (с учётом усиления).
function applyLine0Debuff(state, target, sourceLabel, deltaAtk) {
  if (!target) return;
  // Применяем как permanent buff с отрицательной атакой, sourceId
  // содержит ссылку на источник чтобы можно было снять.
  // Используем applyTemporaryBuff с expiresAtTurn=Infinity? Нет, лучше
  // permanent с пометкой «while_source_alive» — снимается через
  // removePermanentBuffsBySource при пересчёте.
  // applyPermanentBuff поддерживает только +N, для -N делаем напрямую.
  if (deltaAtk >= 0) return;
  target.base.atk = Math.max(0, target.base.atk + deltaAtk);
  target.current.atk = Math.max(0, target.current.atk + deltaAtk);
  if (!Array.isArray(target.permanentBuffs)) target.permanentBuffs = [];
  target.permanentBuffs.push({
    id: `${SUPPRESSION_SOURCE}:line0`,
    label: sourceLabel,
    atk: deltaAtk,
    armor: 0,
    hp: 0
  });
}

// Применить Молчание на N ходов к цели (line-1 эффект). Снимаем
// предыдущее, если уже было от этого источника, и применяем заново.
function applyLine1SilenceForTurns(state, source, target, turns) {
  if (!target || turns <= 0) return;
  // Уникальный sourceId на источник + цель — чтобы snap при смерти
  // источника можно было идентифицировать.
  const srcKey = `${SUPPRESSION_SOURCE}:line1:src${source.instanceId || 'x'}`;
  // Лёгкий вариант: используем стандартный applySilence с источником.
  // Снятие при смерти источника — через recomputeSuppression (там мы
  // вызываем removeSuppression для всех целей у которых был наш srcKey).
  // Поскольку silence.js не отслеживает «N ходов», запишем срок в
  // state.suppressionSilenceExpiry[srcKey] = currentTurn + turns.
  // Декремент в начале каждого хода: см. tickSuppressionSilenceExpiry.
  applySilence(state, target, srcKey);
  if (!state.suppressionSilenceExpiry) state.suppressionSilenceExpiry = {};
  state.suppressionSilenceExpiry[srcKey] = (state.currentTurn || 0) + turns;
  // Сохраняем ссылку source→sourceKey для уборки при смерти.
  if (!source.suppressionSilenceKey) source.suppressionSilenceKey = srcKey;
  // Сохраняем target для возможного removeSilence.
  if (!state.suppressionSilenceTargets) state.suppressionSilenceTargets = {};
  state.suppressionSilenceTargets[srcKey] = target;
}

// Пересчёт всего Подавления. Снимает все наши permanent-баффы (line0),
// применяет заново по живым источникам. Line1-эффект (Молчание) — oneshot,
// здесь не переприменяется, только снимается при смерти источника.
export function recomputeSuppression(state) {
  if (!state?.players) return;
  // 1) Снять все наши permanent-баффы Подавления с обоих игроков.
  for (const p of state.players) {
    if (!p?.field) continue;
    for (let line = 0; line < 2; line++) {
      const row = p.field[line];
      if (!row) continue;
      for (const u of row) {
        if (!u) continue;
        removePermanentBuffsBySource(state, u, suppressionMatcher);
      }
    }
  }
  // 2) Снять line1-Молчания если источник умер или замолк.
  if (state.suppressionSilenceTargets) {
    const keepKeys = new Set();
    // Соберём все валидные ключи живых источников.
    for (let pIdx = 0; pIdx < state.players.length; pIdx++) {
      const p = state.players[pIdx];
      if (!p?.field) continue;
      for (const row of p.field) {
        if (!row) continue;
        for (const u of row) {
          if (!u || !isSuppressionSource(u)) continue;
          if (u.suppressionSilenceKey) keepKeys.add(u.suppressionSilenceKey);
        }
      }
    }
    // Снимаем Молчание у целей чей источник пропал.
    for (const [key, target] of Object.entries(state.suppressionSilenceTargets)) {
      if (keepKeys.has(key)) continue;
      removeSilence(state, target, key);
      delete state.suppressionSilenceTargets[key];
      if (state.suppressionSilenceExpiry) delete state.suppressionSilenceExpiry[key];
    }
  }

  // 3) Применить line0-дебафы для всех активных источников.
  for (let pIdx = 0; pIdx < state.players.length; pIdx++) {
    const p = state.players[pIdx];
    if (!p?.field) continue;
    for (let line = 0; line < 2; line++) {
      const row = p.field[line];
      if (!row) continue;
      for (const source of row) {
        if (!source || !isSuppressionSource(source)) continue;
        if (line !== 0) continue;
        const base = -1;
        const delta = amplifierActive(state, pIdx) ? base * 2 : base;
        const label = `Подавление (${source.name_ru})`;
        const targets = [];
        const mirror = getMirrorCell(state, pIdx, source);
        if (mirror) targets.push(mirror);
        if (extenderActive(state, pIdx) && source.faction === 'global') {
          const other = getOtherLineCell(state, pIdx, source);
          if (other) targets.push(other);
        }
        for (const t of targets) applyLine0Debuff(state, t, label, delta);
      }
    }
  }
}

// Вызывается при выставлении источника Подавления — line=1 oneshot Молчание.
// Применяется ровно в момент Order'а / попадания на поле.
export function onSuppressionSourcePlaced(state, source) {
  if (!source || !isSuppressionSource(source)) return;
  const sourceOwner = source.ownerId;
  if (sourceOwner == null) return;
  // line=0 эффект (атак-дебаф) — учтётся в recomputeSuppression.
  if (source.line === 1) {
    const baseTurns = 2;
    const turns = amplifierActive(state, sourceOwner) ? 3 : 2;
    const targets = [];
    const mirror = getMirrorCell(state, sourceOwner, source);
    if (mirror) targets.push(mirror);
    if (extenderActive(state, sourceOwner) && source.faction === 'global') {
      const other = getOtherLineCell(state, sourceOwner, source);
      if (other) targets.push(other);
    }
    for (const t of targets) applyLine1SilenceForTurns(state, source, t, turns);
  }
  // Пересчёт для line0 источников и обновления.
  recomputeSuppression(state);
}

// Декремент сроков Молчания от Подавления — вызывать в Ф1 рядом с
// expireTemporaryBuffs.
export function tickSuppressionSilenceExpiry(state) {
  if (!state?.suppressionSilenceExpiry) return;
  const expiry = state.suppressionSilenceExpiry;
  const currentTurn = state.currentTurn || 0;
  for (const key of Object.keys(expiry)) {
    if (currentTurn >= expiry[key]) {
      const target = state.suppressionSilenceTargets?.[key];
      if (target) removeSilence(state, target, key);
      delete expiry[key];
      if (state.suppressionSilenceTargets) delete state.suppressionSilenceTargets[key];
    }
  }
}
